import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2, UserMinus } from "lucide-react";

export function LostReasonsCard({ start, end }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    api.get("/enquiries/analytics/lost-reasons", { params: end ? { start, end } : { start } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ total: 0, reasons: [], notes: [] }); });
    return () => { cancelled = true; };
  }, [start, end]);

  const max = Math.max(1, ...(data?.reasons || []).map((r) => r.count));

  return (
    <Card className="p-4 rounded-md shadow-none border border-zinc-200 space-y-3" data-testid="analytics-lost-reasons">
      <div className="flex items-center justify-between">
        <div className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
          <UserMinus className="h-3.5 w-3.5" /> Why we lose people
          <span className="normal-case tracking-normal text-zinc-400 ml-1">· marked Lost in this window</span>
        </div>
        {data && <div className="text-2xs text-zinc-500">Total lost: <b className="text-zinc-900 font-mono-num" data-testid="lost-total">{data.total}</b></div>}
      </div>
      {!data ? (
        <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
      ) : data.total === 0 ? (
        <div className="text-xs text-zinc-500 italic">Nobody was marked Lost in this window.</div>
      ) : (
        <>
          <ul className="space-y-1.5">
            {data.reasons.map((r) => (
              <li key={r.reason} className="grid grid-cols-[180px_1fr_70px] items-center gap-2 text-xs" data-testid={`lost-reason-row-${r.reason}`}>
                <span className="text-zinc-800 truncate">{r.label}</span>
                <span className="h-2.5 rounded-full bg-zinc-100 overflow-hidden">
                  <span className="block h-full bg-zinc-800 rounded-full transition-[width] duration-500" style={{ width: `${(r.count / max) * 100}%` }} />
                </span>
                <span className="text-right font-mono-num text-zinc-700">{r.count} · {Math.round((r.pct || 0) * 100)}%</span>
              </li>
            ))}
          </ul>
          {data.notes.length > 0 && (
            <div>
              <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1">Recent notes</div>
              <ul className="space-y-1" data-testid="lost-notes">
                {data.notes.map((n, i) => (
                  <li key={i} className="text-xs text-zinc-700 border-l-2 border-zinc-200 pl-2">
                    <b className="text-zinc-900">{n.name || "Unnamed"}</b> · {n.reason} · {new Date(n.at).toLocaleDateString()} — {n.note}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
